<template>
  <div class="smart smart-select" :class="{multi:allowMultiple}" @focus="prev=selectedKeys.map(s=>s)" @blur="onBlur()" tabindex="0">
    <div class="values-box" @click="showOptions=!showOptions">
      <div class="values" :class="{delete:showDelete && (selectedOptions.length >= 2 || allowNone)}" style="white-space: pre;">
        <div class="value-box" v-for="option in selectedOptions" :key="option.key">
          <div class="control light value">
            <div><slot name="value" :option="option" :showDelete="showDelete">{{option.label}}</slot></div>
            <div class="icon cross" @click.stop="onOptionClick(option)"></div>
          </div>
        </div>
        <div v-if="this.selectedOptions.length==0" class="value">
          <slot
            name="placeholder"
            :placeholder="placeholder"
          >{{typeof this.placeholder === "object" ? this.getLabel(this.placeholder) : this.placeholder}}</slot>
        </div>
      </div>
      <div class="icon arrow-down"></div>
    </div>
    <div class="options-box" style="white-space: pre;" :class="{open:showOptions}">
      <div
        v-show="(!option.disabled || showDisabled) && option.visible"
        class="control option"
        v-for="option in processedOptions"
        :key="option.key"
        @click="onOptionClick(option)"
        :class="{selected:option.selected,disabled:option.disabled}"
      >
        <slot name="value" :option="option">{{option.label}}</slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DotlogixSelect",
  props: {
    value: {
      default: null
    },
    placeholder: {
      type: [String, Object]
    },
    options: {
      type: [Array, Function],
      default(){return [];}
    },
    optionKey: {
      type: [String, Function],
      default: "key"
    },
    optionLabel: {
      type: [String, Function],
      default: "label"
    },
    optionDisabled: {
      type: [String, Function],
      default: "disabled"
    },
    optionVisible: {
      type: [String, Function],
      default: "visible"
    },
    allowMultiple: {
      type: Boolean,
      default: false
    },
    showDisabled: {
      type: Boolean,
      default: true
    },
    showDelete: {
      type: Boolean,
      default: true
    },
    allowNone: {
      type: Boolean,
      default: false
    },
    noneValue: {
      type: [Object],
    //   default() {
    //     return {
    //       key: "",
    //       label: " ",
    //       disabled: false,
    //       selected: false,
    //       value: null
    //     };
    //   }
    },
    closeAfterSelect: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showOptions: false,
      dataSource: [],
      prev: []
    };
  },
  watch: {
    options: {
      handler: "fetchData",
      immediate: true
    }
  },
  computed: {
    processedOptions() {
      if (!this.dataSource) return [];
      var processed = this.dataSource.map(o =>
        this.createOption(o, this.selectedKeys)
      );
      if (this.allowNone && !this.allowMultiple && this.noneValue)
        processed = [this.noneValue, ...processed];
      return processed;
    },
    selectedKeys() {
      if (!this.value) return [];

      if (Array.isArray(this.value)) {
        return this.allowMultiple
          ? this.value.map(this.getKey)
          : [this.getKey(this.value[0])];
      } else {
        return [this.getKey(this.value)];
      }
    },
    selectedOptions() {
      if(this.allowMultiple && this.noneValue){
        // eslint-disable-next-line
        this.noneValue.selected = false;
        var options = this.processedOptions.filter(o => o.selected);
        // eslint-disable-next-line
        this.noneValue.selected = options.length == 0;
        return options.length > 0 ? options : [this.noneValue]
      }else{
        return this.processedOptions.filter(o => o.selected);
      }
    }
  },
  methods: {
    createOption(option, selectedKeys) {
      var key = this.getKey(option);
      return {
        key,
        label: this.getLabel(option),
        disabled: this.getDisabled(option),
        selected: selectedKeys.indexOf(key) !== -1,
        visible: this.getVisible(option),
        value: option
      };
    },
    getKey(option, defaultValue) {
      return this.getValue(option, this.optionKey) || defaultValue || "";
    },
    getLabel(option, defaultValue) {
      return this.getValue(option, this.optionLabel) || defaultValue || "";
    },
    getDisabled(option, defaultValue) {
      var value = this.getValue(option, this.optionDisabled);
      return value != null
        ? value
        : defaultValue != null
        ? defaultValue
        : false;
    },
    getVisible(option, defaultValue) {
      var value = this.getValue(option, this.optionVisible);
      return value != null ? value : defaultValue != null ? defaultValue : true;
    },
    getValue(option, selector) {
      if (!option) return null;

      if (!selector) return option;

      if (typeof selector === "string") return option[selector];

      return selector(option);
    },
    fetchData() {
      if (!this.options) {
        this.dataSource = [];
      } else if (Array.isArray(this.options)) {
        this.dataSource = this.options;
      } else {
        Promise.resolve(this.options()).then(
          value => (this.dataSource = value),
          error => {
            this.dataSource = [];
            // eslint-disable-next-line
            console.log(error);
          }
        );
      }
    },
    onOptionClick(option) {
      if (!option || option.disabled) return;
      var currentValue = this.value;
      var changed = false;
      if (this.allowMultiple) {
        if (currentValue && !Array.isArray(currentValue))
          currentValue = [currentValue];
        if (!currentValue || currentValue.length === 0) {
          currentValue = option.value ? [option.value] : [];
          changed = true;
        } else if (option.value) {
          var index = currentValue.findIndex(
            o => this.getKey(o) === option.key
          );
          if (index >= 0) {
            if (currentValue.length >= 2 || this.allowNone) {
              currentValue.splice(index, 1);
              changed = true;
            }
          } else if (currentValue) {
            currentValue.push(option.value);
            changed = true;
          }
        }
      } else if (currentValue !== option.value) {
        currentValue = option.value;
        changed = true;
      }
      if (changed) this.$emit("input", currentValue);
      if (this.autoCloseOnSelect) this.showOptions = false;
    },
    onBlur() {
      this.showOptions = false;
      var changed =
        this.selectedKeys.length !== this.prev.length ||
        this.selectedKeys.some((v, i) => v !== this.prev[i]);
      if (changed) this.$emit("change", this.value);
    }
  }
};
</script>

<style>
* {
  box-sizing: border-box;
}

.smart-select {
  display: block;
  position: relative;

  color: var(--input-foreground);
  max-width: 100%;
}

.smart-select:focus {
  outline: none;
}

.smart-select .values-box,
.smart-select .options-box {
  background: var(--input-background);
  border: var(--input-border);
  box-sizing: border-box;
  padding-right: 4px;
}

.smart-select .values-box{
    display: grid;
    grid-template-columns: 1fr minmax(16px, min-content);
}

.smart-select .options-box {
  position: absolute;
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: min-content;
  grid-row-gap: 4px;
  width: 100%;
  transform: scaleY(0);
  transform-origin: top;
  transition: transform ease-out 0.25s;
  overflow: hidden;
  border-top: none;
  z-index: 1;
}

.smart-select .options-box.open {
  transform: scaleY(1);
}

.smart-select .values {
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  overflow: hidden;
  text-overflow: ellipsis;

  --value-devider-content: ",";
  --value-devider-margin: 0 0 0 2px;
  --value-devider-padding: initial;
}

.smart-select:focus-within .values-box {
  border-color: var(--accent);
}

.smart-select .option-box,
.smart-select .value-box {
  display: flex;
}

.smart-select .value-box:not(:last-child)::after {
  content: var(--value-devider-content);
  margin: var(--value-devider-margin);
  padding: var(--value-devider-padding);
  align-self: center;
}

.smart-select .option,
.smart-select .value {
  padding: 4px;
  transition: background ease-out 0.25s;
}

.smart-select .values.delete .value {
  display: grid;
  grid-template-columns: min-content 12px;
  grid-auto-flow: column;
  grid-column-gap: 4px;
}

.smart-select .values .value .icon.cross{
  display: none;
}

.smart-select .values.delete .value .icon.cross{
  display: block;
}


.smart-select .icon.delete {
  display: inline-block;
  object-fit: contain;
  object-position: center;
  height: 18px;
  width: 18px;
  padding: 4px;
}
</style>
